import { useEffect, useState } from "react";

export type Theme =
  | "light"
  | "sea-glass"
  | "blueprint"
  | "rose"
  | "dark"
  | "dark-sea-glass"
  | "dark-blueprint"
  | "dark-rose";

type LegacyTheme = "glacier" | "dark-glacier";
type StoredTheme = Theme | LegacyTheme;

export const themes = [
  "light",
  "sea-glass",
  "blueprint",
  "rose",
  "dark",
  "dark-sea-glass",
  "dark-blueprint",
  "dark-rose",
] as const;

export const darkThemes: Theme[] = ["dark", "dark-sea-glass", "dark-blueprint", "dark-rose"];

export const themeLabels: Record<Theme, string> = {
  light: "Parchment",
  "sea-glass": "Sea Glass Parchment",
  blueprint: "Blueprint Parchment",
  rose: "Rose Parchment",
  dark: "Charcoal Parchment",
  "dark-sea-glass": "Dark Sea Glass",
  "dark-blueprint": "Dark Blueprint",
  "dark-rose": "Dark Rose",
};

export function isTheme(value: string | null): value is Theme {
  return value !== null && themes.includes(value as Theme);
}

export function normalizeStoredTheme(value: string | null): Theme | null {
  if (value === "glacier") return "light";
  if (value === "dark-glacier") return "dark";
  return isTheme(value) ? value : null;
}

export function getPreferredTheme(): Theme {
  if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }

  return "light";
}

export function resolveTheme(
  storedTheme: string | null,
  fallbackTheme: Theme = getPreferredTheme()
): Theme {
  return normalizeStoredTheme(storedTheme) ?? fallbackTheme;
}

export function applyTheme(theme: Theme, options?: { persist?: boolean }) {
  const root = document.documentElement;
  root.classList.toggle("dark", darkThemes.includes(theme));
  root.dataset.theme = theme;

  if (options?.persist !== false) {
    localStorage.setItem("theme", theme);
  }
}

export function useTheme(fallbackTheme?: Theme): [Theme, (theme: Theme) => void] {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      return resolveTheme(localStorage.getItem("theme"), fallbackTheme);
    }
    return fallbackTheme ?? "light";
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = (t: Theme) => setThemeState(t);

  return [theme, setTheme];
}
